import React, { useMemo } from 'react'
import {
  CrowdfundingActivityStatusEnum,
  presaleActivityList_marketingModule_crowdfundingActivityHomePage_edges_node,
} from '~/graphqls/types'
import { MyImage } from '~/components/ui/MyImage'
import Link from 'next/link'
import dayjs from 'dayjs'
dayjs.extend(require('dayjs/plugin/duration'))
import classNames from 'classnames'
import { useCountDown } from '~/hooks/useCountDown'
import { VipIcon } from '~/components/icon/Vip'
import { to400Image } from '~/utils'

const TEXT_LIMIT = 150

export const SinglePresale: React.FC<{
  data: presaleActivityList_marketingModule_crowdfundingActivityHomePage_edges_node
  className?: string
}> = ({ data, className }) => {
  const { countdownStr: startCountdownStr, countdown: startDowncount } =
    useCountDown(data.countdown)
  const { countdownStr: endCountdownStr, countdown: endDowncount } =
    useCountDown(data.crowdfunding?.countdown)

  const status = useMemo(() => {
    if (
      data.status === CrowdfundingActivityStatusEnum.UN_START &&
      startDowncount <= 0
    ) {
      return CrowdfundingActivityStatusEnum.START
    } else if (
      (data.status === CrowdfundingActivityStatusEnum.START ||
        data.status === CrowdfundingActivityStatusEnum.PRESALE_END) &&
      endDowncount <= 0
    ) {
      return CrowdfundingActivityStatusEnum.SUCCESS
    }
    return data.status
  }, [data, startDowncount, endDowncount])

  const precent = useMemo(() => {
    const goalAmount = data?.crowdfunding?.goalAmount
    const soldAmount = data?.crowdfunding?.soldAmount
    // 0.07 * 100 => 7.000000000000001
    return `${(Number((soldAmount / goalAmount).toFixed(2)) * 100).toFixed(0)}%`
  }, [data])

  const PresaleCreator = (
    <div
      className={classNames(
        'mb-4',
        'p-1',
        'rounded-[90px]',
        'bg-white',
        'bg-opacity-20',
        'inline-block'
      )}
    >
      <div className="flex justify-start items-center">
        <div className="relative w-8 h-8">
          <MyImage
            tag="v400"
            className="w-full h-full rounded-[50%]"
            src={data?.crowdfunding?.creator?.avatarUrl}
          />
          <VipIcon
            userRole={data?.crowdfunding?.creator?.userRoleInfo}
            className="h-3 -right-[3px]  max-w-[15px]"
          />
        </div>
        <div className="font-medium text-base text-neutral-800 px-3">
          {data?.crowdfunding?.creator?.nickname}
        </div>
      </div>
    </div>
  )

  const PresaleContent = (
    <div className="flex justify-between items-center w-full">
      <div className="w-[400px] relative z-20">
        <div
          className={classNames(
            'mb-4',
            'text-neutral-800',
            'text-xl',
            'font-semibold'
          )}
        >
          {data.crowdfunding?.name}
        </div>
        {PresaleCreator}
        <div
          className={classNames(
            'line-clamp-2',
            'mb-4',
            'text-xs',
            'text-neutral-800',
            'font-normal',
            'break-all'
          )}
        >
          <div className="inline">
            {(data?.crowdfunding?.description || '').substring(0, TEXT_LIMIT)}
          </div>
        </div>
        {status === CrowdfundingActivityStatusEnum.END && (
          <div className="w-full block text-neutral-900 text-base py-3 font-semibold rounded-[90px] bg-neutral-400 text-center">
            预售成功
          </div>
        )}
        {(status === CrowdfundingActivityStatusEnum.FAILED ||
          status === CrowdfundingActivityStatusEnum.FAILED_REFUNDED) && (
          <div className="w-full block text-neutral-900 text-base py-3 font-semibold rounded-[90px] bg-neutral-400 text-center">
            预售失败
          </div>
        )}
        {status === CrowdfundingActivityStatusEnum.UN_START && (
          <div className="flex w-full text-neutral-50 text-base font-semibold">
            <div className="cursor-pointer py-3 px-8 rounded-[90px] bg-[#FFD166] text-center whitespace-nowrap">
              即将开始
            </div>
            <div className="py-3 px-8 rounded-[90px] border-2 border-neutral-900/20 text-neutral-600 text-center ml-4 whitespace-nowrap">
              倒计时 {startCountdownStr}
            </div>
          </div>
        )}
        {status === CrowdfundingActivityStatusEnum.SUCCESS && (
          <div className="w-full block cursor-pointer text-neutral-900 text-base py-3 font-semibold rounded-[90px] bg-[#7851EC] text-center">
            进行中 {precent}
          </div>
        )}
        {(status === CrowdfundingActivityStatusEnum.START ||
          status === CrowdfundingActivityStatusEnum.PRESALE_END) && (
          <div className="flex w-full text-neutral-900 text-base font-semibold">
            <div className="cursor-pointer py-3 px-8 rounded-[90px] bg-[#7851EC] text-center whitespace-nowrap">
              进行中 {precent}
            </div>
            <div className="py-3 px-8 rounded-[90px] border-2 border-neutral-900/20 text-neutral-600 text-center ml-4 whitespace-nowrap">
              剩余时间 {endCountdownStr}
            </div>
          </div>
        )}
        {status === CrowdfundingActivityStatusEnum.PUBLIC && (
          <div className="w-full block cursor-pointer text-neutral-900 text-base py-3 font-semibold rounded-[90px] bg-[#45B36B] text-center">
            公售期
          </div>
        )}
      </div>
      <MyImage
        src={data?.crowdfunding?.primaryImage}
        tag="v400"
        style={{
          width: 310,
          height: 310,
          borderRadius: 8,
        }}
      ></MyImage>
    </div>
  )
  return (
    <div className={className}>
      <Link href={`/presale/${data.id}`}>
        <a>
          <div
            className="h-[430px] flex justify-between items-center w-full px-[50px] py-[30px] bg-no-repeat bg-center bg-cover relative before:content-[''] before:absolute before:left-0 before:right-0 before:top-0 before:bottom-0 before:bg-black before:bg-opacity-50 before:backdrop-filter before:backdrop-blur-3xl rounded-2xl overflow-hidden"
            style={{
              backgroundImage: `url(${to400Image(
                data?.crowdfunding?.primaryImage as string
              )})`,
            }}
          >
            {PresaleContent}
          </div>
        </a>
      </Link>
    </div>
  )
}
